import Head from 'next/head'
import { useRouter } from 'next/router'
import { Provider } from 'react-redux'
import { CacheProvider } from '@emotion/react'
import { appWithTranslation } from 'next-i18next'
import { useEffect, useCallback } from 'react'
import Script from 'next/script'
import dynamic from 'next/dynamic'

import { store } from 'src/store'
import { createEmotionCache } from 'src/@core/utils/create-emotion-cache'
import { defaultACLObj } from 'src/configs/acl'
import { useTranslation } from 'src/utils/i18n'
import '../../styles/globals.css'
import useReviewSupportChat from 'src/hooks/useReviewSupportChat'
import { persistReviewBridgeFromQuery } from 'src/utils/reviewBridge'
import { installLangHeaderForFetch } from 'src/utils/requestLang'

// 少量页面直接用 fetch 调后端（支付、评论等），统一补 lang 头，后端据此返回对应语言的 message
installLangHeaderForFetch()

const UserLayout = dynamic(() => import('src/layouts/UserLayout'), { ssr: false })
const AclGuard = dynamic(() => import('src/@core/components/auth/AclGuard'), { ssr: false })
const AuthGuard = dynamic(() => import('src/@core/components/auth/AuthGuard'), { ssr: false })
const GuestGuard = dynamic(() => import('src/@core/components/auth/GuestGuard'), { ssr: false })
const WindowWrapper = dynamic(() => import('src/@core/components/window-wrapper'), { ssr: false })
const ThemeComponent = dynamic(() => import('src/@core/theme/ThemeComponent'), { ssr: false })
const Spinner = dynamic(() => import('src/@core/components/spinner'), { ssr: false })
const ReactHotToast = dynamic(() => import('src/@core/styles/libs/react-hot-toast'), { ssr: false })
const ErrorBoundary = dynamic(() => import('src/components/ErrorBoundary'), { ssr: false })
const AuthProvider = dynamic(() => import('src/context/AuthContext').then(m => m.AuthProvider), { ssr: false })
const SettingsProvider = dynamic(() => import('src/@core/context/settingsContext').then(m => m.SettingsProvider), { ssr: false })
const SettingsConsumer = dynamic(() => import('src/@core/context/settingsContext').then(m => m.SettingsConsumer), { ssr: false })
const Toaster = dynamic(() => import('react-hot-toast').then(mod => mod.Toaster), { ssr: false })
const Analytics = dynamic(() => import('@vercel/analytics/react').then(mod => mod.Analytics), { ssr: false })
const Guide = dynamic(() => import('src/hooks/guide'), { ssr: false })

const clientSideEmotionCache = createEmotionCache()

// iTalks 客服脚本地址：由 NEXT_PUBLIC_ITALKS_BASE_URL 按环境注入，未配置时回退正式地址
const ITALKS_BASE_URL = (process.env.NEXT_PUBLIC_ITALKS_BASE_URL || 'https://app.italks.ai').replace(/\/+$/, '')

// ** Hooks **
const useNProgress = () => {
  useEffect(() => {
    import('src/configs/themeConfig').then(({ default: config }) => {
      if (!config.routingLoader) return
      Promise.all([import('nprogress'), import('nprogress/nprogress.css')]).then(([NProgressModule]) => {
        const np = NProgressModule.default
        const { Router } = require('next/router')
        Router.events.on('routeChangeStart', np.start)
        Router.events.on('routeChangeError', np.done)
        Router.events.on('routeChangeComplete', np.done)
      })
    })
  }, [])
}

const useViewportHeight = () => {
  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh2', `${vh}px`)
    }
    setViewportHeight()
    window.addEventListener('resize', setViewportHeight)

    return () => window.removeEventListener('resize', setViewportHeight)
  }, [])

  useEffect(() => {
    const u = navigator.userAgent
    if (!/\(i[^;]+;( U;)? CPU.+Mac OS X/.test(u)) return
    const setViewHeight = () => {
      const vh = window.innerHeight / 100
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }
    setViewHeight()
  }, [])
}

const usePageDuration = () => {
  const router = useRouter()
  useEffect(() => {
    localStorage.setItem('duration', new Date())
  }, [router.query])

  useEffect(() => {
    const handleLeave = () => {
      const leave = ((new Date() - new Date(localStorage.getItem('duration'))) / 1000).toFixed(2)
    }
    router.events.on('beforeHistoryChange', handleLeave)
    window.addEventListener('beforeunload', handleLeave)

    return () => {
      router.events.off('beforeHistoryChange', handleLeave)
      window.removeEventListener('beforeunload', handleLeave)
    }
  }, [router.query])
}

const useRouteAnalytics = () => {
  const router = useRouter()
  const handleRouteChange = useCallback(url => {
    window.dataLayer?.push({ event: "pageView", pagePath: url, pageTitle: document.title })
  }, [])

  useEffect(() => {
    handleRouteChange(router.asPath)
    router.events.on("routeChangeComplete", handleRouteChange)

    return () => router.events.off("routeChangeComplete", handleRouteChange)
  }, [handleRouteChange, router.asPath])
}

// ** Guard Component **
const Guard = ({ children, authGuard, guestGuard }) => {
  if (guestGuard) return <GuestGuard fallback={<Spinner />}>{children}</GuestGuard>
  if (!authGuard) return <>{children}</>

  return <AuthGuard fallback={<Spinner />}>{children}</AuthGuard>
}

// ** App Component **
const App = props => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props
  const router = useRouter()
  const { asPath, query, pathname } = router
  const { changeLocale } = useTranslation('common')

  const contentHeightFixed = Component.contentHeightFixed ?? false
  const mobileFixedViewport = Component.mobileFixedViewport ?? false
  const getLayout =
    Component.getLayout ?? (
      page => (
        <UserLayout
          contentHeightFixed={contentHeightFixed}
          mobileFixedViewport={mobileFixedViewport}
        >
          {page}
        </UserLayout>
      )
    )
  const setConfig = Component.setConfig ?? undefined
  const authGuard = Component.authGuard ?? true
  const guestGuard = Component.guestGuard ?? false
  const aclAbilities = Component.acl ?? defaultACLObj

  useNProgress()
  useViewportHeight()
  usePageDuration()
  useRouteAnalytics()
  // 好评引导差评路径：review_flow=bad → 自动开 iTalks
  useReviewSupportChat()

  // 外链 ?locale= 强制切换语言
  useEffect(() => {
    if (query.locale) changeLocale(query.locale)
  }, [query.locale])

  // 路由变化时持久化 review_flow / review_source
  useEffect(() => {
    if (!router.isReady) return
    persistReviewBridgeFromQuery(query)
  }, [router.isReady, query])

  // 动态导入其他样式
  useEffect(() => {
    import('prismjs/themes/prism-tomorrow.css')
    import('react-perfect-scrollbar/dist/css/styles.css')
    import('src/iconify-bundle/icons-bundle-react')
  }, [])

  // ** Special Path 判断**
  const specialPaths = ['user-guide-ios', 'user-guide-android', 'back-up-android', 'back-up-ios', 'guide']
  const isSpecialPath = specialPaths.some(p => asPath.includes(p))

  // PC 端恢复 We Are Here；不再在移动端自动写入 gbyte_wbw 隐藏
  useEffect(() => {
    const handler = () => {
      if (window.innerWidth > 768) {
        document.cookie = 'gbyte_wbw=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
      }
    }
    handler()
    window.addEventListener('resize', handler)

    return () => window.removeEventListener('resize', handler)
  }, [])

  return (
    <Provider store={store}>
      <CacheProvider value={emotionCache}>
        <Head>
          <title>{`${process.env.NEXT_PUBLIC_SITE_TYPE} `}</title>
          <meta name="description" content={process.env.NEXT_PUBLIC_SITE_TYPE} />
          <meta name="keywords" content={process.env.NEXT_PUBLIC_SITE_TYPE} />
          <meta name="viewport" content="initial-scale=1, width=device-width" />
        </Head>

        <Script
          src={pathname.split('/')[3] === 'checkout' || pathname.split('/')[4] === 'checkout'
            ? process.env.NEXT_PUBLIC_PATTERN : ''}
          strategy="beforeInteractive"
        />

        {/* 注入全局配置 */}
        <Script id="chat-plugin-config" strategy="beforeInteractive">
          {`
          window.ChatPluginConfig = {
            style: {
              chatWrap: {
                bottom: 187,
                right: 40
              }
            }
          };
        `}
        </Script>

        {/* 加载插件 JS */}
        <Script
          src={`${ITALKS_BASE_URL}/js/67e696ec851d.js?id=4C1E45FFSPYX&toggleBtnStyle=withText`}
          strategy="afterInteractive"
        />

        <Guide />

        <AuthProvider>
          <SettingsProvider {...(setConfig ? { pageSettings: setConfig() } : {})}>
            <SettingsConsumer>
              {({ settings }) => (
                <ThemeComponent settings={settings}>
                  <WindowWrapper>
                    <Guard authGuard={authGuard} guestGuard={guestGuard}>
                      <AclGuard aclAbilities={aclAbilities} guestGuard={guestGuard}>
                        {isSpecialPath ? (
                          <ErrorBoundary asPath={asPath}><Component {...pageProps} /></ErrorBoundary>
                        ) : getLayout(<ErrorBoundary asPath={asPath}><Component {...pageProps} /></ErrorBoundary>)}
                      </AclGuard>
                    </Guard>
                  </WindowWrapper>
                  <ReactHotToast>
                    <Toaster position={settings.toastPosition} toastOptions={{ className: 'react-hot-toast' }} />
                  </ReactHotToast>
                </ThemeComponent>
              )}
            </SettingsConsumer>
          </SettingsProvider>
        </AuthProvider>
        <Analytics />
      </CacheProvider>
    </Provider>
  )
}

export default appWithTranslation(App)
